/**
 * Track geometry: spline, arc-length sampling, flow field, build mask.
 *
 * Three things live here and they are deliberately coupled, because they are
 * three views of the same curve:
 *
 *  1. A centripetal Catmull-Rom spline through hand-authored control points,
 *     resampled at exactly 1px of arc length. Sample index == distance in
 *     pixels, which is what makes `Enemy.progress` a plain number.
 *  2. A tile-resolution flow field. Each cell stores a unit direction toward
 *     a point further along the track, so an enemy that a shockwave has flung
 *     into open ground still knows which way downstream is, for one array read.
 *  3. The buildable mask, which is just the corridor stamped into tiles.
 *
 * Every query writes into a caller-supplied object. Nothing here allocates
 * after construction.
 */

import { VIEW } from '@/core/config';
import type { World } from '@/core/types';

// ────────────────────────────────────────────────────────────────── shapes ──

/** A hand-authored map: control points plus a half-width at each one. */
export interface TrackMap {
  name: string;
  /** One line for the map-select screen. */
  blurb: string;
  /** Control points, xy interleaved, buffer pixels. Ends may sit off-screen. */
  points: readonly number[];
  /** Corridor half-width in pixels at each control point. */
  widths: readonly number[];
}

/** A point on the centreline. */
export interface TrackSample {
  x: number;
  y: number;
  /** Unit tangent, pointing toward increasing progress. */
  tx: number;
  ty: number;
  /** Corridor half-width here. */
  w: number;
}

/** A projection of an arbitrary point onto the centreline. */
export interface TrackHit extends TrackSample {
  /** Arc distance of the closest centreline point, in pixels. */
  progress: number;
  /** Lateral distance. Positive is to the left of the direction of travel. */
  offset: number;
  /** Absolute lateral distance. */
  dist: number;
}

export const makeTrackSample = (): TrackSample => ({ x: 0, y: 0, tx: 1, ty: 0, w: 1 });
export const makeTrackHit = (): TrackHit =>
  ({ x: 0, y: 0, tx: 1, ty: 0, w: 1, progress: 0, offset: 0, dist: 0 });

// ─────────────────────────────────────────────────────────────────── maps ──

/**
 * SERPENTINE — the long one. Three full vertical lobes marching left to right,
 * ~1600px of corridor. Towers placed in the gaps between lobes cover two
 * strands at once, which is the whole lesson of the map.
 */
export const MAP_SERPENTINE: TrackMap = {
  name: 'serpentine',
  blurb: 'Three lobes, sixteen hundred pixels. Every gap covers two strands.',
  points: [
    -40, 300,
     50, 300,
    110, 250,
    120, 170,
     96,  92,
    150,  48,
    230,  50,
    280, 110,
    292, 190,
    300, 268,
    360, 310,
    432, 296,
    470, 228,
    468, 150,
    446,  80,
    500,  40,
    572,  52,
    608, 118,
    600, 200,
    614, 280,
    680, 320,
  ],
  widths: [
    20, 20, 19, 19, 19, 19, 19, 19, 19, 19, 19,
    19, 19, 19, 19, 20, 20, 20, 20, 20, 20,
  ],
};

/**
 * CROSSROADS — a lemniscate walked end to end, so the path passes through the
 * centre twice and crosses itself at roughly ninety degrees both times. The
 * crossing tile is the most valuable real estate in the game and the swarm
 * behaviour there is worth watching: two strands of the same flock brush past
 * each other and the separation force makes them braid.
 *
 * Control points are a Bernoulli lemniscate (a = 250, centred on the buffer)
 * sampled every 22.5 degrees, with the two centre passes nudged 6px apart so
 * the strands cross rather than touch.
 */
export const MAP_CROSSROADS: TrackMap = {
  name: 'crossroads',
  blurb: 'A figure eight that crosses itself twice. Own the centre, own the map.',
  points: [
    700,  92,   // entry tail, off-screen right
    628, 122,
    570, 180,   // right lobe tip
    521, 257,
    438, 263,
    372, 228,
    317, 183,   // crossing A
    268, 132,
    202,  97,
    118, 103,
     70, 180,   // left lobe tip
    118, 257,
    202, 263,
    268, 228,
    323, 177,   // crossing B
    372, 132,
    438,  97,
    452,  40,   // exit rise
    462, -40,   // off-screen top
  ],
  widths: [
    18, 18, 18, 18, 18, 18, 17, 18, 18, 18,
    18, 18, 18, 18, 17, 18, 18, 18, 18,
  ],
};

/**
 * VORTEX — the short one, ~1350px, one and a half turns of an Archimedean
 * spiral collapsing into a core at the centre of the buffer. Arms sit 130px
 * apart so a single long-range tower can rake three of them; a short-range
 * tower parked in the middle sees the whole endgame and nothing else.
 *
 * Radius runs 165 → 32 over 60-degree steps, x scaled 1.5 to fill the 16:9
 * buffer.
 */
export const MAP_VORTEX: TrackMap = {
  name: 'vortex',
  blurb: 'A spiral that ends in the core. Short, tight, and it all comes to you.',
  points: [
    700, 180,   // entry tail
    640, 180,
    567, 180,
    433, 310,
    218, 297,
    139, 180,
    241,  88,
    388, 101,
    434, 180,
    366, 233,
    285, 220,
    272, 180,
    320, 180,   // the core
  ],
  widths: [
    17, 17, 17, 17, 17, 17, 17, 16, 16, 16, 15, 15, 14,
  ],
};

/**
 * OUTPOST — the teaching map, and deliberately the poorest one. ~900px of
 * corridor pushed into the top-left two thirds of the buffer, so a third of the
 * board is buildable, useless, and visibly so. One gentle lobe, one wide bend,
 * no pockets that see two strands.
 *
 * The width is the other half of the lesson: 22px of corridor against
 * serpentine's 19 means a tower placed off the ideal tile still reaches, which
 * is what a first act should forgive.
 */
export const MAP_OUTPOST: TrackMap = {
  name: 'outpost',
  blurb: 'One bend and a lobe. Wide, forgiving, and most of the board is scenery.',
  points: [
    -40, 196,
     70, 194,
    150, 162,
    202,  94,
    282,  74,
    352, 122,
    398, 202,
    478, 250,
    558, 214,
    680, 202,
  ],
  widths: [22, 22, 22, 21, 21, 21, 21, 22, 22, 22],
};

/**
 * CAUSEWAY — four horizontal runs stacked down the buffer and stitched at the
 * ends, ~1,900px. The point is the *interior*: every tile between two runs sees
 * both of them, so the middle band is worth roughly double what the outer band
 * is, and there is a lot of middle band.
 *
 * This is the first map that rewards a wide board rather than a tall one.
 */
export const MAP_CAUSEWAY: TrackMap = {
  name: 'causeway',
  blurb: 'Four runs folded back on themselves. The middle band covers two at once.',
  points: [
    -40,  46,
    110,  42,
    270,  48,
    424,  56,
    548,  86,
    588, 146,
    528, 190,
    382, 196,
    226, 200,
    116, 214,
     66, 262,
    126, 304,
    272, 312,
    428, 308,
    556, 300,
    700, 296,
  ],
  widths: [
    19, 19, 19, 19, 19, 18, 18, 19, 19, 19,
    18, 18, 19, 19, 19, 19,
  ],
};

/**
 * LATTICEWORK — five full-height traversals, ~2,300px, corridor down to 16px.
 * Strands sit ~140px apart, which is inside a tier-2 Turret's reach and well
 * inside a Sniper's, so a tower parked between two strands is a tower firing
 * twice. Nearly every buildable tile is within reach of something.
 *
 * This is the act where the board stops being a line of towers beside a path
 * and starts being a field the path is threaded through.
 */
export const MAP_LATTICEWORK: TrackMap = {
  name: 'latticework',
  blurb: 'Five traversals, a hundred and forty pixels apart. The whole board fires.',
  points: [
    -40,  44,
     36,  40,
     66, 110,
     62, 190,
     58, 262,
     96, 314,
    150, 318,
    176, 254,
    170, 176,
    166,  98,
    200,  48,
    252,  46,
    282, 112,
    278, 192,
    274, 264,
    312, 316,
    366, 318,
    392, 254,
    386, 176,
    382,  98,
    414,  48,
    468,  46,
    496, 112,
    492, 192,
    488, 264,
    526, 316,
    580, 318,
    606, 250,
    616, 170,
    700, 148,
  ],
  widths: [
    17, 16, 16, 16, 16, 16, 16, 16, 16, 16,
    16, 16, 16, 16, 16, 16, 16, 16, 16, 16,
    16, 16, 16, 16, 16, 16, 16, 16, 17, 17,
  ],
};

/**
 * MAELSTROM — the longest, ~2,700px. A spiral inward to the core and a second
 * spiral back out, so the track passes every radius twice at different arc
 * distances. A tower on the mid radius covers four passes.
 *
 * `Track.localProgress` is what makes this legal: the inbound and outbound arms
 * cross the same pixels a thousand pixels apart in arc length, and a global
 * nearest-point search would teleport a body between them.
 */
export const MAP_MAELSTROM: TrackMap = {
  name: 'maelstrom',
  blurb: 'In to the core and back out. Every radius is walked twice.',
  points: [
    700,  40,
    560,  36,
    400,  34,
    240,  40,
    120,  76,
     56, 150,
     70, 240,
    140, 302,
    250, 330,
    390, 332,
    502, 306,
    566, 250,
    560, 186,
    500, 146,
    414, 138,
    330, 152,
    272, 196,
    272, 246,
    322, 274,
    386, 268,
    414, 226,
    386, 190,
    336, 202,
    340, 240,
    404, 320,
    520, 350,
    640, 356,
  ],
  widths: [
    20, 20, 20, 19, 19, 19, 19, 19, 19, 19, 19, 18, 18, 17,
    17, 16, 15, 14, 14, 13, 13, 12, 12, 13, 15, 17, 18,
  ],
};

export const MAPS: Record<string, TrackMap> = {
  outpost: MAP_OUTPOST,
  serpentine: MAP_SERPENTINE,
  crossroads: MAP_CROSSROADS,
  vortex: MAP_VORTEX,
  causeway: MAP_CAUSEWAY,
  latticework: MAP_LATTICEWORK,
  maelstrom: MAP_MAELSTROM,
};

export const MAP_NAMES: readonly string[] = [
  'outpost', 'serpentine', 'crossroads', 'vortex', 'causeway', 'latticework', 'maelstrom',
];

// ────────────────────────────────────────────────────────── spline maths ──

/**
 * Centripetal Catmull-Rom. Uniform parameterisation cusps and self-intersects
 * whenever two control points sit close together, which is exactly what
 * happens at the tight end of the spiral; the alpha = 0.5 knot spacing does
 * not, at the cost of a few square roots at build time.
 */
const seg = { x: 0, y: 0 };

function knot(ax: number, ay: number, bx: number, by: number, t: number): number {
  const d = Math.sqrt(Math.hypot(bx - ax, by - ay));
  return t + (d < 1e-4 ? 1e-4 : d);
}

function catmullRom(
  p0x: number, p0y: number, p1x: number, p1y: number,
  p2x: number, p2y: number, p3x: number, p3y: number,
  u: number,
): void {
  const t0 = 0;
  const t1 = knot(p0x, p0y, p1x, p1y, t0);
  const t2 = knot(p1x, p1y, p2x, p2y, t1);
  const t3 = knot(p2x, p2y, p3x, p3y, t2);
  const t = t1 + (t2 - t1) * u;

  const a1 = (t1 - t) / (t1 - t0), b1 = (t - t0) / (t1 - t0);
  const a2 = (t2 - t) / (t2 - t1), b2 = (t - t1) / (t2 - t1);
  const a3 = (t3 - t) / (t3 - t2), b3 = (t - t2) / (t3 - t2);

  const A1x = a1 * p0x + b1 * p1x, A1y = a1 * p0y + b1 * p1y;
  const A2x = a2 * p1x + b2 * p2x, A2y = a2 * p1y + b2 * p2y;
  const A3x = a3 * p2x + b3 * p3x, A3y = a3 * p2y + b3 * p3y;

  const c1 = (t2 - t) / (t2 - t0), d1 = (t - t0) / (t2 - t0);
  const c2 = (t3 - t) / (t3 - t1), d2 = (t - t1) / (t3 - t1);

  const B1x = c1 * A1x + d1 * A2x, B1y = c1 * A1y + d1 * A2y;
  const B2x = c2 * A2x + d2 * A3x, B2y = c2 * A2y + d2 * A3y;

  seg.x = a2 * B1x + b2 * B2x;
  seg.y = a2 * B1y + b2 * B2y;
}

const smooth = (u: number): number => u * u * (3 - 2 * u);

// ───────────────────────────────────────────────────────────────── track ──

/** Acceleration grid cell for nearest-point queries, in pixels. */
const NEAR_CELL = 8;
/** How far ahead the flow field looks, in pixels of arc length. */
const FLOW_LOOKAHEAD = 34;
/** Extra clearance around the corridor where towers may not be placed. */
const BUILD_MARGIN = 9;

export class Track {
  readonly name: string;
  /** Centreline, xy interleaved, one sample per pixel of arc length. */
  readonly points: Float32Array;
  /** Unit tangents, xy interleaved, same indexing as `points`. */
  readonly tangents: Float32Array;
  /** Half-width at each sample. */
  readonly width: Float32Array;
  readonly count: number;
  /** Arc length in pixels. Equal to `count - 1`. */
  readonly length: number;

  /** Unit direction toward increasing progress, per tile, xy interleaved. */
  readonly flow: Float32Array;
  /** Arc distance of the nearest centreline point, per tile. */
  readonly flowProgress: Float32Array;
  /** Non-zero means a tower may stand there. */
  readonly buildable: Uint8Array;

  private readonly nearIndex: Int32Array;
  private readonly nearCols: number;
  private readonly nearRows: number;

  constructor(map: TrackMap) {
    this.name = map.name;

    const dense = densify(map);
    const resampled = resample(dense);
    this.points = resampled.points;
    this.tangents = resampled.tangents;
    this.width = resampled.width;
    this.count = resampled.count;
    this.length = resampled.count - 1;

    this.nearCols = Math.ceil(VIEW.width / NEAR_CELL);
    this.nearRows = Math.ceil(VIEW.height / NEAR_CELL);
    this.nearIndex = new Int32Array(this.nearCols * this.nearRows);
    this.buildNearIndex();

    this.flow = new Float32Array(VIEW.cols * VIEW.rows * 2);
    this.flowProgress = new Float32Array(VIEW.cols * VIEW.rows);
    this.buildable = new Uint8Array(VIEW.cols * VIEW.rows);
    this.buildFields();
  }

  // ── sampling ──

  /** Centreline position, tangent and width at an arc distance. */
  sampleAt(progress: number, out: TrackSample): TrackSample {
    let p = progress;
    if (p < 0) p = 0;
    else if (p > this.length) p = this.length;

    const i = p | 0;
    const f = p - i;
    const j = i < this.count - 1 ? i + 1 : i;
    const a = i * 2, b = j * 2;
    const P = this.points, T = this.tangents;

    out.x = P[a] + (P[b] - P[a]) * f;
    out.y = P[a + 1] + (P[b + 1] - P[a + 1]) * f;

    let tx = T[a] + (T[b] - T[a]) * f;
    let ty = T[a + 1] + (T[b + 1] - T[a + 1]) * f;
    const m = Math.hypot(tx, ty);
    if (m > 1e-6) { tx /= m; ty /= m; } else { tx = 1; ty = 0; }
    out.tx = tx;
    out.ty = ty;
    out.w = this.width[i] + (this.width[j] - this.width[i]) * f;
    return out;
  }

  /**
   * `sampleAt` with the ends extended along their tangents. Spawn tails run
   * off-screen by design, so an enemy queued at progress -40 has to stand 40px
   * behind the entry rather than piled on top of it.
   */
  sampleExtended(progress: number, out: TrackSample): TrackSample {
    this.sampleAt(progress, out);
    if (progress < 0) {
      out.x += out.tx * progress;
      out.y += out.ty * progress;
    } else if (progress > this.length) {
      const d = progress - this.length;
      out.x += out.tx * d;
      out.y += out.ty * d;
    }
    return out;
  }

  /**
   * Projects a world point onto the centreline. The nearest-index grid gives a
   * seed within half a cell diagonal, so a fixed +/-64 sample window around it
   * is always enough; that keeps this O(1) instead of O(track length).
   */
  project(x: number, y: number, out: TrackHit): TrackHit {
    let cx = (x / NEAR_CELL) | 0;
    let cy = (y / NEAR_CELL) | 0;
    if (cx < 0) cx = 0; else if (cx >= this.nearCols) cx = this.nearCols - 1;
    if (cy < 0) cy = 0; else if (cy >= this.nearRows) cy = this.nearRows - 1;

    const seed = this.nearIndex[cy * this.nearCols + cx];
    let lo = seed - 72; if (lo < 0) lo = 0;
    let hi = seed + 72; if (hi > this.count - 1) hi = this.count - 1;

    const P = this.points;
    let best = seed;
    let bestD2 = Infinity;
    for (let i = lo; i <= hi; i++) {
      const dx = x - P[i * 2], dy = y - P[i * 2 + 1];
      const d2 = dx * dx + dy * dy;
      if (d2 < bestD2) { bestD2 = d2; best = i; }
    }

    // Sub-pixel refinement: project onto whichever adjacent unit segment the
    // point actually falls inside.
    let prog = best;
    if (best < this.count - 1) {
      const ax = P[best * 2], ay = P[best * 2 + 1];
      const bx = P[best * 2 + 2], by = P[best * 2 + 3];
      const ex = bx - ax, ey = by - ay;
      const len2 = ex * ex + ey * ey;
      if (len2 > 1e-9) {
        let t = ((x - ax) * ex + (y - ay) * ey) / len2;
        if (t > 0) { if (t > 1) t = 1; prog = best + t; }
      }
    }
    if (best > 0) {
      const ax = P[best * 2 - 2], ay = P[best * 2 - 1];
      const bx = P[best * 2], by = P[best * 2 + 1];
      const ex = bx - ax, ey = by - ay;
      const len2 = ex * ex + ey * ey;
      if (len2 > 1e-9) {
        let t = ((x - ax) * ex + (y - ay) * ey) / len2;
        if (t < 1 && t > 0) {
          const px = ax + ex * t, py = ay + ey * t;
          const d2 = (x - px) * (x - px) + (y - py) * (y - py);
          const qx = P[(prog | 0) * 2], qy = P[(prog | 0) * 2 + 1];
          const cur = (x - qx) * (x - qx) + (y - qy) * (y - qy);
          if (d2 < cur) prog = best - 1 + t;
        }
      }
    }

    this.sampleAt(prog, out);
    out.progress = prog;
    // Left of travel is positive: cross(tangent, delta) with y pointing down.
    const dx = x - out.x, dy = y - out.y;
    out.offset = out.tx * dy - out.ty * dx;
    out.dist = Math.abs(out.offset);
    return out;
  }

  /** Arc distance of the closest centreline point to (x, y). */
  nearestProgress(x: number, y: number): number {
    return this.project(x, y, scratch).progress;
  }

  /**
   * Nearest progress restricted to an arc-length window around `from`.
   *
   * A global nearest-point search is wrong for anything that follows the
   * track: on `crossroads` the two strands pass through the same pixel 700px
   * apart in arc length, so a swarmling entering the crossing would teleport a
   * whole lobe forward. Searching in arc space instead of screen space makes
   * that impossible — 90px of window cannot reach an adjacent strand, because
   * adjacent strands are always hundreds of pixels apart *along the curve*.
   */
  localProgress(x: number, y: number, from: number, window: number): number {
    const P = this.points;
    let lo = Math.floor(from - window); if (lo < 0) lo = 0;
    let hi = Math.ceil(from + window); if (hi > this.count - 1) hi = this.count - 1;
    if (hi <= lo) return from;

    const stride = 3;
    let best = lo, bestD2 = Infinity;
    for (let i = lo; i <= hi; i += stride) {
      const dx = x - P[i * 2], dy = y - P[i * 2 + 1];
      const d2 = dx * dx + dy * dy;
      if (d2 < bestD2) { bestD2 = d2; best = i; }
    }
    let a = best - stride; if (a < lo) a = lo;
    let b = best + stride; if (b > hi) b = hi;
    for (let i = a; i <= b; i++) {
      const dx = x - P[i * 2], dy = y - P[i * 2 + 1];
      const d2 = dx * dx + dy * dy;
      if (d2 < bestD2) { bestD2 = d2; best = i; }
    }
    return best;
  }

  /** Signed lateral distance from the centreline. Left of travel is positive. */
  signedOffset(x: number, y: number): number {
    return this.project(x, y, scratch).offset;
  }

  /** Corridor half-width nearest to (x, y). */
  widthAt(x: number, y: number): number {
    return this.project(x, y, scratch).w;
  }

  // ── flow field ──

  /** Tile index for a buffer-space point, clamped to the grid. */
  tileIndex(x: number, y: number): number {
    let tx = (x / VIEW.tile) | 0;
    let ty = (y / VIEW.tile) | 0;
    if (tx < 0) tx = 0; else if (tx >= VIEW.cols) tx = VIEW.cols - 1;
    if (ty < 0) ty = 0; else if (ty >= VIEW.rows) ty = VIEW.rows - 1;
    return ty * VIEW.cols + tx;
  }

  /**
   * One array read plus a clamp. This is the cheap steering hint for agents
   * that have drifted; it is not accurate enough to path with on its own,
   * which is why the flock blends it against a progress-driven seek.
   */
  flowAt(x: number, y: number, out: { x: number; y: number }): void {
    const i = this.tileIndex(x, y) * 2;
    out.x = this.flow[i];
    out.y = this.flow[i + 1];
  }

  /** Rough arc distance from the flow field, without a projection search. */
  flowProgressAt(x: number, y: number): number {
    return this.flowProgress[this.tileIndex(x, y)];
  }

  buildableAt(x: number, y: number): boolean {
    return this.buildable[this.tileIndex(x, y)] !== 0;
  }

  // ── geometry the balance model reads ──

  /**
   * Fraction of the centreline a tower standing on each buildable tile can see,
   * sorted best first. Index `i` is the coverage of the `i`-th best slot.
   *
   * This is the measured root of `Balance.COVERAGE`, which was a flat guess of
   * 0.60 across every map. A tower's contribution to a round is proportional to
   * how much of a body's transit it can shoot at, and that is a property of the
   * *map*, not of the tower — so a map with the corridor folded back on itself
   * is worth more per tower than one with a single pass, and this function is
   * where that difference stops being an assertion.
   *
   * Allocates. Build-time and tooling only; never call it per frame.
   */
  slotCoverage(range: number): number[] {
    const P = this.points;
    const n = this.count;
    // 4px stride over the centreline. At the ranges towers actually have (72 to
    // 260px) that is a sub-percent error on the covered fraction and it keeps a
    // 500-tile sweep under a few million distance tests.
    const stride = 4;
    const r2 = range * range;
    const half = VIEW.tile * 0.5;
    const out: number[] = [];

    for (let ty = 0; ty < VIEW.rows; ty++) {
      for (let tx = 0; tx < VIEW.cols; tx++) {
        if (this.buildable[ty * VIEW.cols + tx] === 0) continue;
        const px = tx * VIEW.tile + half;
        const py = ty * VIEW.tile + half;
        let seen = 0;
        let total = 0;
        for (let i = 0; i < n; i += stride) {
          total++;
          const dx = px - P[i * 2], dy = py - P[i * 2 + 1];
          if (dx * dx + dy * dy <= r2) seen++;
        }
        if (seen > 0) out.push(seen / Math.max(1, total));
      }
    }
    out.sort((a, b) => b - a);
    return out;
  }

  /**
   * Buildable tiles that can see any part of the track from `range`. The floor
   * under an act's authored slot budget: a map cannot support more towers than
   * it has places to put them.
   */
  reachableSlots(range: number): number {
    return this.slotCoverage(range).length;
  }

  /**
   * Mean track coverage across the best `slots` tiles at `range` — what
   * `Balance.COVERAGE` should be for this map and this board width.
   *
   * Overlap between towers is deliberately not deduplicated. `COVERAGE` scales
   * *summed* loadout DPS, so the quantity wanted is the per-tower average, not
   * the union.
   */
  meanCoverage(range: number, slots: number): number {
    const all = this.slotCoverage(range);
    if (all.length === 0) return 0;
    const n = Math.min(slots, all.length);
    let sum = 0;
    for (let i = 0; i < n; i++) sum += all[i];
    return sum / n;
  }

  // ── wiring ──

  /** Copies geometry and the build mask into the shared world container. */
  writeInto(world: World): void {
    world.track.points = this.points;
    world.track.tangents = this.tangents;
    world.track.width = this.width;
    world.track.length = this.length;
    if (world.buildable.length === this.buildable.length) world.buildable.set(this.buildable);
    else world.buildable = Uint8Array.from(this.buildable);
  }

  // ── construction ──

  private buildNearIndex(): void {
    const P = this.points;
    const n = this.count;
    // Coarse pass with a stride, then a local exact pass. The stride bound is
    // safe because consecutive samples are exactly 1px apart.
    const stride = 4;
    for (let cy = 0; cy < this.nearRows; cy++) {
      const py = cy * NEAR_CELL + NEAR_CELL * 0.5;
      for (let cx = 0; cx < this.nearCols; cx++) {
        const px = cx * NEAR_CELL + NEAR_CELL * 0.5;
        let best = 0, bestD2 = Infinity;
        for (let i = 0; i < n; i += stride) {
          const dx = px - P[i * 2], dy = py - P[i * 2 + 1];
          const d2 = dx * dx + dy * dy;
          if (d2 < bestD2) { bestD2 = d2; best = i; }
        }
        let lo = best - stride; if (lo < 0) lo = 0;
        let hi = best + stride; if (hi > n - 1) hi = n - 1;
        for (let i = lo; i <= hi; i++) {
          const dx = px - P[i * 2], dy = py - P[i * 2 + 1];
          const d2 = dx * dx + dy * dy;
          if (d2 < bestD2) { bestD2 = d2; best = i; }
        }
        this.nearIndex[cy * this.nearCols + cx] = best;
      }
    }
  }

  private buildFields(): void {
    const hit = makeTrackHit();
    const ahead = makeTrackSample();
    const half = VIEW.tile * 0.5;

    for (let ty = 0; ty < VIEW.rows; ty++) {
      for (let tx = 0; tx < VIEW.cols; tx++) {
        const idx = ty * VIEW.cols + tx;
        const px = tx * VIEW.tile + half;
        const py = ty * VIEW.tile + half;

        this.project(px, py, hit);
        this.flowProgress[idx] = hit.progress;

        // Aim at a point further downstream, then fold in the local tangent so
        // a cell sitting on the centreline still reads as "straight ahead"
        // rather than chasing a point it is already heading toward.
        const target = hit.progress + FLOW_LOOKAHEAD;
        this.sampleAt(target > this.length ? this.length : target, ahead);
        let dx = ahead.x - px, dy = ahead.y - py;
        const m = Math.hypot(dx, dy);
        if (m > 1e-4) { dx /= m; dy /= m; } else { dx = hit.tx; dy = hit.ty; }
        let fx = dx * 0.72 + hit.tx * 0.28;
        let fy = dy * 0.72 + hit.ty * 0.28;
        const fm = Math.hypot(fx, fy);
        if (fm > 1e-4) { fx /= fm; fy /= fm; } else { fx = hit.tx; fy = hit.ty; }
        this.flow[idx * 2] = fx;
        this.flow[idx * 2 + 1] = fy;

        // A tile is blocked if any part of it falls inside the corridor plus a
        // clearance ring, so towers never overhang the path edge.
        this.buildable[idx] = hit.dist > hit.w + BUILD_MARGIN + half ? 1 : 0;
      }
    }
  }
}

const scratch = makeTrackHit();

// ────────────────────────────────────────────────────── build-time helpers ──

interface Dense { x: number[]; y: number[]; w: number[] }

/** Evaluates the spline into a fine polyline. Allocation here is load-time. */
function densify(map: TrackMap): Dense {
  const n = map.points.length >> 1;
  if (n < 2) throw new Error(`track "${map.name}" needs at least two control points`);
  if (map.widths.length !== n) {
    throw new Error(`track "${map.name}" has ${map.widths.length} widths for ${n} points`);
  }

  const cx = (i: number): number => map.points[Math.max(0, Math.min(n - 1, i)) * 2];
  const cy = (i: number): number => map.points[Math.max(0, Math.min(n - 1, i)) * 2 + 1];

  const out: Dense = { x: [], y: [], w: [] };
  for (let i = 0; i < n - 1; i++) {
    // Reflect the endpoints so the curve starts and ends on the outer controls.
    const p0x = i === 0 ? 2 * cx(0) - cx(1) : cx(i - 1);
    const p0y = i === 0 ? 2 * cy(0) - cy(1) : cy(i - 1);
    const p3x = i === n - 2 ? 2 * cx(n - 1) - cx(n - 2) : cx(i + 2);
    const p3y = i === n - 2 ? 2 * cy(n - 1) - cy(n - 2) : cy(i + 2);

    const chord = Math.hypot(cx(i + 1) - cx(i), cy(i + 1) - cy(i));
    const steps = Math.max(8, Math.min(192, Math.ceil(chord * 0.75)));
    const w0 = map.widths[i], w1 = map.widths[i + 1];

    for (let s = 0; s < steps; s++) {
      const u = s / steps;
      catmullRom(p0x, p0y, cx(i), cy(i), cx(i + 1), cy(i + 1), p3x, p3y, u);
      out.x.push(seg.x);
      out.y.push(seg.y);
      out.w.push(w0 + (w1 - w0) * smooth(u));
    }
  }
  out.x.push(cx(n - 1));
  out.y.push(cy(n - 1));
  out.w.push(map.widths[n - 1]);
  return out;
}

interface Resampled {
  points: Float32Array;
  tangents: Float32Array;
  width: Float32Array;
  count: number;
}

/** Walks the polyline emitting one sample per pixel of arc length. */
function resample(d: Dense): Resampled {
  const m = d.x.length;
  let total = 0;
  for (let i = 1; i < m; i++) total += Math.hypot(d.x[i] - d.x[i - 1], d.y[i] - d.y[i - 1]);

  const count = Math.max(2, Math.floor(total) + 1);
  const points = new Float32Array(count * 2);
  const tangents = new Float32Array(count * 2);
  const width = new Float32Array(count);

  let seg0 = 0;
  let walked = 0;
  let segLen = Math.hypot(d.x[1] - d.x[0], d.y[1] - d.y[0]);

  for (let k = 0; k < count; k++) {
    const want = k;
    while (seg0 < m - 2 && walked + segLen < want) {
      walked += segLen;
      seg0++;
      segLen = Math.hypot(d.x[seg0 + 1] - d.x[seg0], d.y[seg0 + 1] - d.y[seg0]);
    }
    const t = segLen > 1e-6 ? Math.min(1, (want - walked) / segLen) : 0;
    points[k * 2] = d.x[seg0] + (d.x[seg0 + 1] - d.x[seg0]) * t;
    points[k * 2 + 1] = d.y[seg0] + (d.y[seg0 + 1] - d.y[seg0]) * t;
    width[k] = d.w[seg0] + (d.w[seg0 + 1] - d.w[seg0]) * t;
  }

  // Central differences over the 1px samples. A three-tap window is smooth
  // enough for sprite rotation and steering without lagging real corners.
  for (let k = 0; k < count; k++) {
    const a = k > 0 ? k - 1 : k;
    const b = k < count - 1 ? k + 1 : k;
    let tx = points[b * 2] - points[a * 2];
    let ty = points[b * 2 + 1] - points[a * 2 + 1];
    const mm = Math.hypot(tx, ty);
    if (mm > 1e-6) { tx /= mm; ty /= mm; } else { tx = 1; ty = 0; }
    tangents[k * 2] = tx;
    tangents[k * 2 + 1] = ty;
  }

  return { points, tangents, width, count };
}

// ───────────────────────────────────────────────────────── active track ──

let active: Track | null = null;

/** The track the enemy systems steer against. Set by `installMap`. */
export function getActiveTrack(): Track | null {
  return active;
}

export function setActiveTrack(t: Track | null): void {
  active = t;
}

/** Builds a named map, writes it into the world, and makes it active. */
export function installMap(world: World, name: string): Track {
  const map = MAPS[name];
  if (!map) throw new Error(`unknown map "${name}"`);
  const track = new Track(map);
  track.writeInto(world);
  active = track;
  return track;
}
